import EmberService, { service } from '@ember/service';
import { tracked } from '@glimmer/tracking';

import type { Service } from 'webapp-types';
import { BillingFeature, BoardPopupShareTab, MetricDetailsTab } from 'webapp/enums/enums';
import { TrackingEvent } from 'webapp/enums/tracking';
import { sortBy } from 'webapp/utils/common';

import type {
	TrackingOriginType as BoardsTrackingOriginType,
	TrackingOriginForTemplateCreated,
} from 'webapp/services/model-manager/boards';
import type { TrackingOriginTypeForNewClientForm } from 'webapp/services/model-manager/spaces';

export interface Progress {
	stage: number;
	stepsCompleted: number[];
}

export interface Stage {
	id: number;
	name: string;
	steps: Step[];
}

export interface Step {
	id: number;
	actions: Action[];
	description: string;
	event: string;
	name: string;
	threshold: number[];
	weight: number;
}

export interface Action {
	articleId: string;
	name: string;
	type: string;
	id?: string;
	page?: string;
	url?: string;
}

export default class AccountProgressService extends EmberService {
	@service('account') declare accountService: Service.Account;
	@service('event-manager') declare eventManagerService: Service.EventManager;
	@service('navigation') declare navigationService: Service.Navigation;
	@service('constants') declare constantsService: Service.Constants;
	@service('current-user') declare currentUserService: Service.CurrentUser;
	@service('user-settings') declare userSettingsService: Service.UserSettings;
	@service('analytics') declare analyticsService: Service.Analytics;
	@service('popup') declare popupService: Service.Popup;
	@service('model-manager/board-shares')
	declare mmBoardSharesService: Service.ModelManager.BoardShares;
	@service('model-manager/boards') declare mmBoardsService: Service.ModelManager.Boards;
	@service('model-manager/board-loops')
	declare mmBoardLoopsService: Service.ModelManager.BoardLoops;
	@service('model-manager/custom-queries')
	declare mmCustomQueriesService: Service.ModelManager.CustomQueries;
	@service('model-manager/goals') declare mmGoalsService: Service.ModelManager.Goals;
	@service('model-manager/metrics') declare mmMetricsService: Service.ModelManager.Metrics;
	@service('model-manager/spaces') declare mmSpacesService: Service.ModelManager.Spaces;
	@service('model-manager/users') declare mmUsersService: Service.ModelManager.Users;
	@service('metric-details') declare metricDetailsService: Service.MetricDetails;
	@service('billing') declare billingService: Service.Billing;

	@tracked private progressData: Progress | null = null;

	get stage(): number {
		return this.progressData?.stage || 1;
	}

	get stepsCompleted(): number[] {
		return this.progressData?.stepsCompleted || [];
	}

	get allData(): { stages: Stage[] } | undefined {
		return this.constantsService?.accountProgress[String(this.accountService.accountType)];
	}

	get stagesData(): Stage[] {
		return this.allData?.stages || [];
	}

	get isAllDone(): boolean {
		const stages = this.stagesData;
		const lastStage = stages[stages.length - 1];

		if (!lastStage) {
			return false;
		}

		if (this.stage > lastStage.id) {
			return true;
		}

		if (this.stage === lastStage.id) {
			return this.activeStageStepsCompleted.length === lastStage.steps.length;
		}

		return false;
	}

	get activeStage(): Stage | null {
		return this.stagesData.find((item) => item.id === this.stage) || null;
	}

	// returning ids of completed steps
	get activeStageStepsCompleted(): number[] {
		const activeStageStepsCompleted: number[] = [],
			activeStage = this.activeStage;

		if (activeStage?.steps) {
			const activeStageStepIds = activeStage.steps.map((item) => item.id);

			(this.stepsCompleted || []).forEach((stepId: number) => {
				if (activeStageStepIds.includes(stepId)) {
					activeStageStepsCompleted.push(stepId);
				}
			});
		}

		return activeStageStepsCompleted;
	}

	get isHiddenBecauseWhitelabeling(): boolean {
		return this.accountService.whiteLabelingEnabled && !this.currentUserService.isAdmin;
	}

	getPointsForStage(stageData: Stage, stepsCompleted: number[]): number {
		let points = 0;

		(stageData.steps || []).forEach((step: Step) => {
			if (stepsCompleted.includes(step.id)) {
				points += step.weight || 0;
			}
		});

		return points;
	}

	get points(): number {
		let points = 0;

		if (this.isAllDone) {
			points = 100;
		} else if (this.activeStage && this.stepsCompleted.length) {
			points = this.getPointsForStage(this.activeStage, this.stepsCompleted);
		}

		return points;
	}

	get pointsWithPercentage(): string {
		return `${+this.points}%`;
	}

	get canSeeAccountProgressSubpage(): boolean {
		return (
			!this.accountService.isClient &&
			!this.accountService.isDemoAccount &&
			!this.isHiddenBecauseWhitelabeling
		);
	}

	update(progressData: Progress): void {
		this.progressData = progressData;
	}

	get introTitle(): string {
		return this.accountService.isAgency
			? 'Account setup'
			: 'Your journey to data-driven starts here.';
	}

	get introText(): string {
		return this.accountService.isAgency
			? 'Complete the following steps to learn how Databox works and to set up reporting for your first accounts. To learn more about our Solutions Partner program and start offering business intelligence services, connect with us here.'
			: 'The Databox onboarding certifications are designed not only to get you set up and familiar with our product, but also practicing the successful habits that have proven to drive the success of data-driven professionals and the companies they work for.';
	}

	doAction(actionData: Action, model: Step, stage: Stage | null, origin: string): void {
		this.analyticsService.event(TrackingEvent.ACCOUNT_PROGRESS_STEP_CLICK, {
			level: stage?.id,
			step: model.id,
			name: model.name,
			origin: origin,
		});

		if (actionData.type === 'url') {
			window.open(actionData.url, '_blank');
		} else if (actionData.type === 'webapp') {
			let route = actionData.page;
			const isAccountSubroute = route?.indexOf('account') === 0;
			if (isAccountSubroute) {
				route = route?.replace('account', 'settings');
			}
			if (route?.startsWith('notifications')) {
				route = 'settings/notifications';
			}
			if (route?.startsWith('forecasts.metric')) {
				this.metricDetailsService.openMetricDetailsDrawer({
					tab: MetricDetailsTab.FORECAST,
				});
				return;
			}
			if ((!isAccountSubroute || this.currentUserService.isAdmin) && actionData.page) {
				this.navigationService.transitionToRoute(actionData.page);
			}
		} else if (actionData.type === 'special') {
			if (actionData.id === 'create-board') {
				this.navigationService.transitionToRoute('designer.board', 'null');
			} else if (actionData.id === 'goal-wizard') {
				if (this.billingService.checkIfCan(BillingFeature.GOALS, true)) {
					void this.mmMetricsService.prepareForMetricEditing();
					this.mmGoalsService.openNewPopup('account-progress');
				}
			} else if (actionData.id === 'popup-snapshot') {
				this.mmBoardSharesService.createNewAndOpenEdit('account_progress');
			} else if (actionData.id === 'board-edit') {
				const latestBoard = sortBy(this.mmBoardsService.normalBoardModels, 'created').pop();
				if (latestBoard) {
					this.navigationService.transitionToRoute('designer.board', latestBoard);
				} else {
					this.navigationService.transitionToRoute('designer');
				}
			} else if (actionData.id === 'account-templates') {
				if (this.mmBoardsService.normalBoardModels.length) {
					this.mmBoardsService.openAccountTemplateEditPopup(
						null,
						origin as TrackingOriginForTemplateCreated,
					);
				} else {
					this.navigationService.transitionToRoute('boards.index');
				}
			} else if (actionData.id === 'invite-user') {
				this.mmUsersService.openUserInvitePopupWithEligibilityChecks({
					trackingOrigin: 'account-progress',
				});
			} else if (actionData.id === 'create-clients') {
				this.mmSpacesService.openCreateClientPopup(
					origin as TrackingOriginTypeForNewClientForm,
				);
			} else if (actionData.id === 'create-loop') {
				this.mmBoardLoopsService.createNewWithTracking();
			} else if (actionData.id === 'share-board') {
				if (this.mmBoardsService.normalBoardModels.length) {
					this.mmBoardsService.openSharePopup({
						model: this.mmBoardsService.normalBoardModels[0]!,
						trackingOrigin: origin as BoardsTrackingOriginType,
						selectedTab: BoardPopupShareTab.LINK,
					});
				} else {
					this.navigationService.transitionToRoute('boards.index');
				}
			} else if (actionData.id === 'create-custom-metric') {
				this.mmCustomQueriesService.openCreateNewPopup('listing');
			}
		}
	}
}

declare module '@ember/service' {
	interface Registry {
		'account-progress': AccountProgressService;
	}
}
